// src/pages/vendor/Chat.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogContent,
  IconButton as MuiIconButton,
  Slider,
  Tooltip,
} from "@mui/material";
import {
  Send,
  Search,
  AttachFile,
  Image,
  Close,
  ArrowBack,
  Refresh,
  PlayArrow,
  Pause,
  Mic,
  Stop,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";

// ============================================================
// UTILITY FUNCTION - ROBUST SANITIZER
// ============================================================
const getFullUrl = (url) => {
  if (!url) return null;
  
  let cleanUrl = String(url).trim();

  // Fix accidental leading slash before http(s)://, e.g. "/https://domain.com/..."
  if (cleanUrl.startsWith('/http://') || cleanUrl.startsWith('/https://')) {
    cleanUrl = cleanUrl.substring(1);
  }

  // Handle blob or data URLs directly
  if (cleanUrl.startsWith('blob:') || cleanUrl.startsWith('data:')) {
    return cleanUrl;
  }

  // If already a complete http/https URL, return it
  if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
    return cleanUrl;
  }

  const baseUrl = process.env.REACT_APP_API_URL || 'https://api.vtech-solution.com';
  
  // Clean /api/ prefix if present
  cleanUrl = cleanUrl.replace(/^\/?(api\/)+/, '');
  
  if (!cleanUrl.startsWith('/')) {
    cleanUrl = '/' + cleanUrl;
  }
  
  const fullUrl = `${baseUrl}${cleanUrl}`;
  return fullUrl;
};

// ============================================================
// VOICE PLAYER COMPONENT
// ============================================================
const VoicePlayer = ({ url, isMine, duration: initialDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const fullUrl = getFullUrl(url);

  useEffect(() => {
    if (!fullUrl) {
      setError(true);
      setIsLoading(false);
      return;
    }

    if (fullUrl.startsWith('blob:')) {
      setIsLoading(false);
      setDuration(initialDuration || 0);
      return;
    }

    const audio = new Audio();
    audio.crossOrigin = "anonymous"; // Enable cross-origin streaming
    audioRef.current = audio;
    audio.preload = "metadata";

    const handleEnded = () => {
      setIsPlaying(false);
      clearInterval(intervalRef.current);
      setCurrentTime(audio.duration || initialDuration || 0);
    };

    const handleError = (e) => {
      console.error('Audio loading error:', fullUrl, e);
      setIsPlaying(false);
      setIsLoading(false);
      setError(true);
      clearInterval(intervalRef.current);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(false);
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      } else {
        setDuration(initialDuration || 0);
      }
    };

    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    audio.src = fullUrl;
    audio.load();

    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setDuration(initialDuration || 0);
        setError(false);
      }
    }, 5000);

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      clearInterval(intervalRef.current);
      clearTimeout(timeoutId);
      audio.pause();
      audio.src = '';
    };
  }, [fullUrl, initialDuration]);

  useEffect(() => {
    if (isPlaying && audioRef.current) {
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          const time = audioRef.current.currentTime;
          if (!isNaN(time) && isFinite(time)) {
            setCurrentTime(time);
            
            const dur = audioRef.current.duration;
            if (dur && !isNaN(dur) && isFinite(dur) && time >= dur && dur > 0) {
              setIsPlaying(false);
              clearInterval(intervalRef.current);
            }
          }
        }
      }, 100);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || error) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      clearInterval(intervalRef.current);
    } else {
      if (audio.currentTime >= (audio.duration || 0)) {
        audio.currentTime = 0;
        setCurrentTime(0);
      }
      
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((err) => {
            console.error('Play failed:', err);
            setIsPlaying(false);
            clearInterval(intervalRef.current);
            setError(true);
          });
      }
    }
  };

  const handleSliderChange = (_, value) => {
    const audio = audioRef.current;
    if (audio && isFinite(value) && !isNaN(value)) {
      const wasPlaying = isPlaying;
      if (wasPlaying) {
        audio.pause();
        clearInterval(intervalRef.current);
      }
      
      audio.currentTime = value;
      setCurrentTime(value);
      
      if (wasPlaying) {
        audio.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(() => {
            setIsPlaying(false);
          });
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds === Infinity) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (error) {
    return (
      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'error.light', borderRadius: 2 }}>
        <Typography variant="caption" color="error" display="block">
          ⚠️ Audio unavailable
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
          {url ? 'File may be corrupted or missing' : 'No audio file'}
        </Typography>
      </Box>
    );
  }

  const displayDuration = duration || initialDuration || 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: isMine ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.04)",
        borderRadius: 2,
        p: 1.5,
        minWidth: "200px",
        mt: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ width: "100%" }}
      >
        <IconButton
          size="small"
          sx={{ color: isMine ? "white" : "primary.main" }}
          onClick={togglePlayPause}
          disabled={isLoading || error}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: isMine ? "white" : "primary.main" }} />
          ) : isPlaying ? (
            <Pause />
          ) : (
            <PlayArrow />
          )}
        </IconButton>

        <Slider
          size="small"
          value={currentTime}
          min={0}
          max={displayDuration || 1}
          onChange={handleSliderChange}
          disabled={isLoading || error}
          sx={{
            flex: 1,
            color: isMine ? "white" : "primary.main",
            "& .MuiSlider-thumb": {
              width: 12,
              height: 12,
            },
          }}
        />
      </Stack>

      <Typography
        variant="caption"
        sx={{
          alignSelf: "flex-end",
          mt: 0.5,
          color: isMine ? "rgba(255,255,255,0.7)" : "text.secondary",
          fontFamily: "monospace",
          fontSize: "0.75rem",
        }}
      >
        {isPlaying || currentTime > 0
          ? formatDuration(currentTime) + " / " + formatDuration(displayDuration)
          : formatDuration(displayDuration)}
      </Typography>
    </Box>
  );
};

// ============================================================
// VOICE RECORDER COMPONENT
// ============================================================
const VoiceRecorder = ({ onSendVoice }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mimeType = 'audio/webm;codecs=opus';
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm',
        });
        
        const url = URL.createObjectURL(blob);
        onSendVoice(url, recordingTime, blob);
        setRecordingTime(0);
        
        stream.getTracks().forEach((track) => track.stop());
      };

      // Request data every 250ms to ensure audio buffers populate properly
      mediaRecorder.start(250);
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      alert("Please allow microphone access to record voice messages.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {isRecording ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "error.main",
              color: "white",
              px: 2,
              py: 0.5,
              borderRadius: 2,
              mr: 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "white",
                mr: 1,
                animation: "pulse 1s infinite",
              }}
            />
            <Typography variant="body2" fontWeight="bold">
              {formatTime(recordingTime)}
            </Typography>
          </Box>
          <IconButton color="error" onClick={stopRecording}>
            <Stop />
          </IconButton>
        </>
      ) : (
        <Tooltip title="Hold to record voice message">
          <IconButton
            size="small"
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
          >
            <Mic />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

// ============================================================
// MAIN CHAT COMPONENT
// ============================================================
const Chat = () => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [ws, setWs] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const blobUrlsRef = useRef(new Map());

  useEffect(() => {
    fetchRooms();
    
    return () => {
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!selectedRoom) return;

    const wsUrl = `${process.env.REACT_APP_WS_URL || "wss://api.vtech-solution.com"}/chat/rooms/${selectedRoom.id}?userId=${user.id}`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      fetchMessages(selectedRoom.id);
    };

    socket.onmessage = (event) => {
      const newMsg = JSON.parse(event.data);
      
      setMessages((prev) => {
        const existingTemp = prev.find(m => 
          m.tempId && newMsg.tempId && m.tempId === newMsg.tempId
        );
        
        if (existingTemp && existingTemp.attachment?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(existingTemp.attachment.url);
          blobUrlsRef.current.delete(existingTemp.tempId);
        }
        
        if (prev.some((m) => m.id === newMsg.id)) return prev;
        const updated = [...prev, newMsg].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
        return updated;
      });
      scrollToBottom();
    };

    socket.onclose = () => {};

    setWs(socket);

    return () => {
      socket.close();
    };
  }, [selectedRoom]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/chat/vendor/rooms`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        },
      );
      const data = await response.json();
      if (data.success) setRooms(data.rooms || []);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/chat/rooms/${roomId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        },
      );
      const data = await response.json();
      if (data.success) {
        const cleanedMessages = (data.messages || []).map(msg => {
          if (msg.attachment && msg.attachment.url) {
            return {
              ...msg,
              attachment: {
                ...msg.attachment,
                url: getFullUrl(msg.attachment.url)
              }
            };
          }
          return msg;
        });
        const sorted = cleanedMessages.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
        setMessages(sorted);
        scrollToBottom();
      }
    } catch (error) {
      // Silent fail
    }
  };

  const sendMessage = (text, attachment = null) => {
    if (!text && !attachment) return;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          message: text || '',
          attachment: attachment,
        }),
      );
      setNewMessage("");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/upload/chat`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
          body: formData,
        },
      );

      const data = await response.json();
      if (data.url) {
        const fullUrl = getFullUrl(data.url);
        const attachment = {
          type: isImage ? "image" : "file",
          url: fullUrl,
          fileName: file.name,
          fileSize: file.size,
        };
        sendMessage("", attachment);
      }
    } catch (error) {
      alert("Failed to upload file. Please try again.");
    }

    event.target.value = "";
  };

  const handleSendVoice = async (blobUrl, duration, audioBlob) => {
    const tempId = 'temp-' + Date.now();
    
    try {
      const tempAttachment = {
        type: 'voice',
        url: blobUrl,
        duration: duration,
      };
      
      const tempMessage = {
        id: tempId,
        tempId: tempId,
        senderId: user.id,
        message: '',
        attachment: tempAttachment,
        createdAt: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, tempMessage]);
      scrollToBottom();
      blobUrlsRef.current.set(tempId, blobUrl);
      
      let blob = audioBlob;
      if (!blob) {
        const response = await fetch(blobUrl);
        blob = await response.blob();
      }
      
      const formData = new FormData();
      formData.append('file', blob, `voice-${Date.now()}.webm`);
      
      const uploadResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/upload/chat`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: formData,
        }
      );
      
      const data = await uploadResponse.json();
      
      if (data.url) {
        const fullUrl = getFullUrl(data.url);
        
        const attachment = {
          type: 'voice',
          url: fullUrl,
          duration: duration,
        };
        
        sendMessage('', attachment);
        setMessages(prev => prev.filter(m => m.tempId !== tempId));
        
        setTimeout(() => {
          if (blobUrlsRef.current.has(tempId)) {
            URL.revokeObjectURL(blobUrl);
            blobUrlsRef.current.delete(tempId);
          }
        }, 5000);
      } else {
        alert('Failed to upload voice message. Please try again.');
        setMessages(prev => prev.filter(m => m.tempId !== tempId));
        if (blobUrlsRef.current.has(tempId)) {
          URL.revokeObjectURL(blobUrl);
          blobUrlsRef.current.delete(tempId);
        }
      }
    } catch (error) {
      console.error('Voice upload error:', error);
      alert('Failed to upload voice message. Please try again.');
      setMessages(prev => prev.filter(m => m.tempId !== tempId));
      if (blobUrlsRef.current.has(tempId)) {
        URL.revokeObjectURL(blobUrl);
        blobUrlsRef.current.delete(tempId);
      }
    }
  };

  const selectRoom = (room) => {
    setSelectedRoom(room);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredRooms = rooms.filter(
    (room) =>
      room.Buyer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      room.Buyer?.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading && rooms.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "calc(100vh - 120px)" }}>
      <Paper
        sx={{
          height: "100%",
          display: "flex",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Chat List Sidebar */}
        <Box
          sx={{
            width: 320,
            borderRight: "1px solid",
            borderColor: "grey.200",
            display: { xs: selectedRoom ? "none" : "flex", md: "flex" },
            flexDirection: "column",
          }}
        >
          <Box
            sx={{ p: 2, borderBottom: "1px solid", borderColor: "grey.200" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6" fontWeight="bold">
                Messages
              </Typography>
              <IconButton size="small" onClick={fetchRooms}>
                <Refresh />
              </IconButton>
            </Stack>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mt: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List sx={{ flex: 1, overflow: "auto" }}>
            {filteredRooms.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No conversations yet
                </Typography>
              </Box>
            ) : (
              filteredRooms.map((room) => (
                <ListItem
                  button
                  key={room.id}
                  onClick={() => selectRoom(room)}
                  sx={{
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "grey.50",
                    bgcolor:
                      selectedRoom?.id === room.id
                        ? "action.selected"
                        : "transparent",
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ListItemAvatar>
                      <Badge
                        color="error"
                        badgeContent={room.unreadCount || 0}
                        invisible={!room.unreadCount}
                      >
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {room.Buyer?.name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <Stack direction="column" spacing={0.2} sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="600">
                        {room.Buyer?.name || "Unknown"}
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 120 }}
                        >
                          {room.lastMessage || "No messages yet"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(room.lastMessageAt || room.updatedAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </ListItem>
              ))
            )}
          </List>
        </Box>

        {/* Chat Area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fafafa",
          }}
        >
          {selectedRoom ? (
            <>
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid",
                  borderColor: "grey.200",
                  bgcolor: "white",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <IconButton
                    sx={{ display: { md: "none" } }}
                    onClick={() => setSelectedRoom(null)}
                  >
                    <ArrowBack />
                  </IconButton>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {selectedRoom.Buyer?.name?.[0]?.toUpperCase() || "U"}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      {selectedRoom.Buyer?.name || "Unknown"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Customer
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                </Stack>
              </Box>

              {/* Messages Area */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {messages.length === 0 ? (
                  <Box sx={{ textAlign: "center", mt: 4 }}>
                    <Typography color="text.secondary">
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.senderId === user?.id;
                    const hasMessage = msg.message && msg.message.trim() !== '';
                    const hasAttachment = msg.attachment && msg.attachment.url;
                    
                    if (!hasMessage && !hasAttachment) return null;
                    
                    return (
                      <Box
                        key={msg.id || msg.tempId}
                        sx={{
                          display: "flex",
                          justifyContent: isMine ? "flex-end" : "flex-start",
                          mb: 2,
                        }}
                      >
                        <Paper
                          sx={{
                            p: 1.5,
                            maxWidth: "70%",
                            bgcolor: isMine ? "primary.main" : "white",
                            color: isMine ? "white" : "text.primary",
                            borderRadius: 2,
                          }}
                        >
                          {/* Image Attachment */}
                          {msg.attachment?.type === "image" && (
                            <Box
                              sx={{ mb: 1, cursor: "pointer" }}
                              onClick={() => {
                                setSelectedImage(getFullUrl(msg.attachment.url));
                                setImageViewerOpen(true);
                              }}
                            >
                              <img
                                src={getFullUrl(msg.attachment.url)}
                                alt="Attachment"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px",
                                  borderRadius: "8px",
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = 
                                    '<span style="color: ' + (isMine ? 'white' : 'text.secondary') + ';">Image unavailable</span>';
                                }}
                              />
                            </Box>
                          )}

                          {/* File Attachment */}
                          {msg.attachment?.type === "file" && (
                            <Box
                              sx={{
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                bgcolor: isMine
                                  ? "rgba(255,255,255,0.15)"
                                  : "rgba(0,0,0,0.05)",
                                borderRadius: 2,
                                p: 1,
                              }}
                            >
                              <AttachFile sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                {msg.attachment.fileName || "Attachment"}
                              </Typography>
                            </Box>
                          )}

                          {/* Voice Message */}
                          {msg.attachment?.type === "voice" && (
                            <VoicePlayer
                              url={msg.attachment.url}
                              isMine={isMine}
                              duration={msg.attachment.duration}
                            />
                          )}

                          {/* Text Message */}
                          {hasMessage && (
                            <Typography variant="body2">
                              {msg.message}
                            </Typography>
                          )}

                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              textAlign: "right",
                              mt: 0.5,
                              color: isMine
                                ? "rgba(255,255,255,0.7)"
                                : "text.secondary",
                            }}
                          >
                            {formatTime(msg.createdAt)}
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid",
                  borderColor: "grey.200",
                  bgcolor: "white",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <AttachFile />
                  </IconButton>

                  <VoiceRecorder onSendVoice={handleSendVoice} />

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(newMessage);
                      }
                    }}
                    multiline
                    maxRows={4}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => sendMessage(newMessage)}
                    disabled={!newMessage.trim()}
                  >
                    <Send />
                  </IconButton>
                </Stack>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a conversation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a customer to start chatting
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent
          sx={{
            p: 0,
            bgcolor: "black",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            position: "relative",
          }}
        >
          <MuiIconButton
            onClick={() => setImageViewerOpen(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "white",
              bgcolor: "rgba(0,0,0,0.5)",
            }}
          >
            <Close />
          </MuiIconButton>
          <img
            src={selectedImage}
            alt="Full View"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = 
                '<Typography color="white" sx={{ p: 4 }}>Image failed to load</Typography>';
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Chat;